
import { getPathTimes } from './AI_functions';
import { formatTime, haversineDistance, dijkstra, bearing, reconstructPath, parseLineString, toCompass, turnInstruction } from './generalHelpers';
import { normalizeRouteName, dedupeRouteNames } from './podcastRouteHelpers';
import {
     getNearestGraphNodeId, pathToLinkIds, dissimilarEnoughByIds, earlyBranchingPass,
     generateNearOptimalAlternativesForDestination
  } from "./podcastRouteHelpers.js";



  
  /* ========= 3.  narrative builder (plain JS, no Gen‑AI)  ============ */
  export function buildTravelNarrative(experiences, startLoc, startTimeSec) {
  
    const header = `**Start Location:** ${startLoc.lat.toFixed(5)}, ${startLoc.lng.toFixed(5)}
  
  **Departure:** ${startTimeSec === 0
      ? "On time"
      : `Delayed by ${formatTime(startTimeSec)}`}
  
  **Assumptions**
  • Digital twin traffic simulation model.
  • All vehicles evacuating at same time from wildfire
  • No acceleration; vehicles move at lane’s max permitted speed.
  • Each vehicle needs 8 m to occupy a lane.
  • Congestion occurs when forward movement stops and vehicles queue.
  
  **Fire Location:** South-East, **Communication Tower**
  `;
  
    const destBlocks = experiences.map((dest, i) => {
      /* --- group consecutive links having the same street name --- */
      const groups = [];
      console.log("Dest blocks", dest)

      dest.destination_experience.forEach(l => {
        const last = groups.at(-1);
        if (last && last.name === l.link_name) {
          last.links.push(l);
        } else {
          groups.push({ name: l.link_name, links: [l] });
        }
      });
  
      /* --- build route summary lines --- */
      const routeLines = groups.map(g => {
        const simTime  = g.links.reduce((s, l) => s + (l.time_taken_to_cross_route || 0), 0);
        const freeTime = g.links.reduce((s, l) => s + Number(l.free_flow_traffic_on_route || 0), 0);
        const delay    = simTime - freeTime;
        return `- **${g.name}** – Road travel time: ${formatTime(simTime)}; Free flow time (w/o traffic) ${formatTime(freeTime)}; Congestion impact: ${formatTime(delay)}`;
      }).join('\n');
  
      const title = `**Destination ${i + 1}: ${dest.destination_name}, Direction:(${dest.destination_direction})**`;
      const total = `**Total time to reach destination:** ${formatTime(dest.total_destination_time)}`;
      const pooling = `**If everyone takes 1 car:** ${formatTime(dest.total_destination_time_pooling)}`;
      const late = `**If you leave 10 minutes late:*** ${formatTime(dest.total_destination_time_lateEvac)}`;
  
      return `${title}\n${total}\n${late}\n${pooling}\n${routeLines}`;
    }).join('\n\n');
  
    return `${header}\n${destBlocks}`;
  }

  export function buildTravelNarrativeV2(experiences, startLoc, startTimeSec) {
    // Use existing formatTime if available; otherwise provide a tiny fallback
    const fmt = (typeof formatTime === "function")
      ? formatTime
      : (s) => {
          const total = Math.max(0, Math.round(Number(s) || 0));
          const h = Math.floor(total / 3600);
          const m = Math.floor((total % 3600) / 60);
          const sec = total % 60;
          if (h > 0) return `${h} hr ${m} min ${sec} sec`;
          if (m > 0) return `${m} min ${sec} sec`;
          return `${sec} sec`;
        };
  
    const lines = [];
  
    // Build one <Exit option> block per destination
    experiences.forEach((dest, i) => {
      // Defensive accessors
      const destName = (dest?.destination_name ?? "Unknown Exit").toString();
      const destDir  = (dest?.destination_direction ?? "").toString();
      const total    = Number(dest?.total_destination_time ?? 0);
      const pooling  = Number(dest?.total_destination_time_pooling ?? 0);
      const late =   Number(dest?.total_destination_time_lateEvac ?? 0)
  
      const segments = Array.isArray(dest?.destination_experience)
        ? dest.destination_experience
        : [];
  
      // Compute total free-flow (sum across all links for this destination)
      let totalFreeFlow = 0;
      for (const l of segments) {
        totalFreeFlow += Number(l?.free_flow_traffic_on_route ?? 0);
      }
  
      // Group consecutive links by street name and aggregate delay per group
      const groups = [];
      for (const l of segments) {
        const name = (l?.link_name ?? "Unnamed Road").toString();
        const sim  = Number(l?.time_taken_to_cross_route ?? 0);
        const ff   = Number(l?.free_flow_traffic_on_route ?? 0);
        const delay = sim - ff;
  
        // consecutive grouping by same street name
        const last = groups.at(-1);
        if (last && last.name === name) {
          last.links.push(l);
          last.simTime += sim;
          last.freeTime += ff;
          last.delay += delay;
        } else {
          groups.push({
            name,
            links: [l],
            simTime: sim,
            freeTime: ff,
            delay
          });
        }
      }
  
      // Determine top 2 congestion “routes” (street groups) by extra delay
      const topCongested = [...groups]
        .sort((a, b) => (b.delay || 0) - (a.delay || 0))
        .slice(0, 2);
  
      // For each congested group, pick a representative link id with max delay
      const congestionPoints = topCongested.map(g => {
        let best = null;
        let bestDelta = -Infinity;
        for (const l of g.links) {
          const sim  = Number(l?.time_taken_to_cross_route ?? 0);
          const ff   = Number(l?.free_flow_traffic_on_route ?? 0);
          const d    = sim - ff;
          if (d > bestDelta) {
            bestDelta = d;
            best = l;
          }
        }
        const linkId =
          (best?.link_id ?? best?.id ?? null) ?? "unknown";
        return {
          name: g.name,
          id: linkId
        };
      });
  
      // Attempt to find destination/route ids if present in input
      const destId =
        dest?.destination_id ?? dest?.id ?? "unknown";
  
      // Try to identify a “route id” from any segment metadata (route_id / path_id)
      let routeId = "unknown";
      for (const l of segments) {
        const maybe =
          l?.route_id ?? l?.path_id ?? l?.route ?? l?.path ?? null;
        if (maybe !== null && maybe !== undefined) {
          routeId = maybe;
          break;
        }
      }
  
      // Optional temporary/nearby destination name, only include tag if present
      const tempDest =
        dest?.temporary_destination ??
        dest?.nearby_destination_name ??
        dest?.closest_destination ??
        null;
  
      // Build the block in required tag format
      lines.push(`<Exit option>`);
      lines.push(``);
      lines.push(`<Name> ${destName}${destDir ? ` (${destDir})` : ""} </Name>`);
      if (tempDest) {
        lines.push(`<Temporary destination> ${tempDest} </Temporary destination>`);
      }
      lines.push(`<Details>`);
      lines.push(`On a normal day (free-flow): ${fmt(totalFreeFlow)}`);
      lines.push(`During the wildfire (according to model): ${fmt(total)}`);
      lines.push(`(Extra ${fmt(total - totalFreeFlow)} due to congestion)`);
      lines.push(`If you leave 10 minutes late, total evacuation time is ${fmt(late)}`)
      lines.push(`<Congestion points>`);
      if (congestionPoints.length === 0) {
        lines.push(`No major congestion points detected.`);
      } else {
        for (const cp of congestionPoints) {
          lines.push(`${cp.name}.`);
        }
      }
      lines.push(`</Congestion points>`);
      lines.push(`</Details>`);
      lines.push(``);
      lines.push(`<Planning steps>`);
      lines.push(`Planning steps:`);
      lines.push(``);
      // Step 1: use pooling value provided
      const deltaPooling = total - pooling;
      lines.push(
        `Step 1: If everyone takes one car per household, your time to evacuate reduces from ${fmt(total)} to ${fmt(pooling)} (saves ${fmt(deltaPooling)}). Plan in advance to take one vehicle.`
      );
      lines.push(``);
      // Step 2: general early-departure guidance (no explicit “early” time available in input)
      lines.push(
        `Step 2: If you receive a Red Flag Warning and leave before a fire starts, you can often avoid most congestion and exit closer to free-flow time (${fmt(totalFreeFlow)}). Leaving early also reduces congestion for others.`
      );
      lines.push(``);
      // Step 3: calm driving guidance
      lines.push(
        `Step 3: Don’t panic during congestion. Abrupt maneuvers or abandoning a vehicle can cause crashes and delays that put everyone at risk. Follow official instructions to minimize chaos and keep moving safely.`
      );
      lines.push(``);
      lines.push(`</Planning steps>`);
      lines.push(``);
      lines.push(`</Exit option>`);
      lines.push(``);
      // IDs section for this destination
      lines.push(`<Route and dest ids example>`);
      const routeLabel = i === 0 ? `${destName} (initial route)` : destName;
      lines.push(`${routeLabel}: id = ${routeId}`);
      if (congestionPoints.length >= 2) {
        lines.push(
          `Congestion points: ${congestionPoints[0].name} (id = ${congestionPoints[0].id}) and ${congestionPoints[1].name} (id = ${congestionPoints[1].id})`
        );
      } else if (congestionPoints.length === 1) {
        lines.push(
          `Congestion points: ${congestionPoints[0].name} (id = ${congestionPoints[0].id})`
        );
      } else {
        lines.push(`Congestion points: none`);
      }
      lines.push(`</Route and dest ids example>`);
      lines.push(``);
  
      // After the first destination, include the Q&A once (as in your example)
      if (i === 0) {
        lines.push(`<Question> Why 3 routes? Why not just stop at 1 </Question>`);
        lines.push(``);
        lines.push(`<Answer>`);
        lines.push(
          `In a fire, knowing only one path is risky because the situation can change quickly — fire spread, smoke, or downed trees can block roads. The model’s details are for planning and won’t always match real-time conditions. Having at least three routes in mind lets you apply your best judgment when conditions shift.`
        );
        lines.push(``);
        lines.push(`</Answer>`);
        lines.push(``);
      }
    });
  
    return `"""\n\n${lines.join("\n")}\n\n"""`;
  }

  export function buildTravelNarrativeV3(experiences, startLoc, startTimeSec, options = {}) {
    const masterRoutes = options?.masterRoutes || {};

    // ---- helpers ----
    const DEST_NAME_MAP = {
      "2151": "Estudillo Avenue",
      "1047": "Grand Avenue",
      "28": "580 North (Highway) near Estudillo"
    };
  
    const pickStartStreet = () => {
      if (typeof startLoc === "string" && startLoc.trim()) return startLoc.trim();
      if (startLoc && typeof startLoc === "object") {
        const candidate =
          startLoc.street ||
          startLoc.name ||
          startLoc.label ||
          (typeof startLoc.address === "string" ? startLoc.address.split(",")[0] : "");
        if (candidate && String(candidate).trim()) return String(candidate).trim();
      }
      return "Lakeview Drive";
    };
  
    // Round seconds to nearest 0.5 minute, return compact "X min" or "H hr M min"
    const halfMin = (sec) => {
      const mins = Math.max(0, Number(sec || 0) / 60);
      const r = Math.round(mins * 2) / 2; // nearest 0.5
      if (r >= 60) {
        const h = Math.floor(r / 60);
        const m = Math.round((r - h * 60) * 2) / 2;
        if (m === 0) return `${h} hr`;
        return `${h} hr ${m} min`;
      }
      return `${r % 1 === 0 ? r : r} min`;
    };
  
    const safeArr = Array.isArray(experiences) ? experiences : [];
    const asArray = (v) => (Array.isArray(v) ? v : []);
    const streetSequence = (segs) => {
      const out = [];
      let last = null;
      for (const s of segs) {
        const nm = (s?.link_name ?? "Unnamed Road").toString();
        if (nm && nm !== last) out.push(nm);
        last = nm;
      }
      return out;
    };
    const totalFreeFlow = (segs) =>
      segs.reduce((sum, l) => sum + Number(l?.free_flow_traffic_on_route || 0), 0);
  
    // Pre-compute data for sorting & rendering
    const enriched = safeArr.map((dest, idx) => {
      const segs = asArray(dest?.destination_experience);
      const groups = [];
      segs.forEach((l) => {
        const name = (l?.link_name ?? "Unnamed Road").toString();
        const sim = Number(l?.time_taken_to_cross_route ?? 0);
        const ff = Number(l?.free_flow_traffic_on_route ?? 0);
        const last = groups[groups.length - 1];
        if (last && last.name === name) {
          last.links.push(l);
          last.simTime += sim;
          last.freeTime += ff;
        } else {
          groups.push({
            name,
            links: [l],
            simTime: sim,
            freeTime: ff,
            firstIndex: groups.length,
          });
        }
      });
  
      const totalFF = totalFreeFlow(segs);
      const total = Number(dest?.total_destination_time || 0);
      const late = Number(dest?.total_destination_time_lateEvac || 0);
      const pooling = Number(dest?.total_destination_time_pooling || 0);
  
      // Resolve destination name and any temp/nearby mapping to titles
      const destIdRaw = (dest?.destination_id ?? dest?.destination_name ?? "").toString();
      const override = destIdRaw && masterRoutes[destIdRaw];
      const rawName = (dest?.destination_name ?? dest?.destination_id ?? "Unknown Exit").toString();
      const mappedName = (override?.title?.trim()) || DEST_NAME_MAP[rawName] || rawName;
  
      const rawTemp =
        dest?.temporary_destination ??
        dest?.nearby_destination_name ??
        dest?.closest_destination ??
        null;
      const mappedTemp =
        (rawTemp && DEST_NAME_MAP[String(rawTemp)]) || (typeof rawTemp === "string" ? rawTemp : null);
  
      const destDir = (dest?.destination_direction ?? "").toString();
  
      // Build a concise ordered list of up to ~4 distinct streets near the start
      const firstSix = groups.slice(0, 9);
      const topByFF = [...firstSix].sort((a, b) => (b.freeTime || 0) - (a.freeTime || 0)).slice(0, 6);
      topByFF.sort((a, b) => a.firstIndex - b.firstIndex);
      const overrideLinks = (override?.links || [])
        .map(l => normalizeRouteName(l?.name))
        .filter(Boolean);
      const routeNames = dedupeRouteNames(
        overrideLinks.length
          ? overrideLinks
          : topByFF.map((g) => normalizeRouteName(g.name))
      );
  
      return {
        idx,
        dest,
        segs,
        groups,
        totalFF,
        total,
        late,
        pooling,
        routeNames,
        name: mappedName,
        tempName: mappedTemp,
        dir: destDir,
      };
    });
  
    // Keep your rule: first route = lower free-flow time
    enriched.sort((a, b) => (a.totalFF || 0) - (b.totalFF || 0));
  
    const startStreet = pickStartStreet();
  
    const humanJoin = (arr) => {
      if (arr.length <= 1) return arr.join("");
      if (arr.length === 2) return `${arr[0]} and ${arr[1]}`;
      return `${arr.slice(0, -1).join(", ")} and ${arr[arr.length - 1]}`;
    };
  
    const lines = [];
  
    // -------- Instructions block (requested) --------
    lines.push(`"""`);
    lines.push(`<Instructions>`);
    lines.push(`Start off the intro as “Welcome to the wildfire evacuation walkthrough for ${startStreet}!”`);
    lines.push(`Introduce yourselves as Alex and Jordan, our AI hosts. Keep the introduction very short and within 10 seconds.`);
    lines.push(`<Intro Section>`);
    lines.push(`Briefly mention the purpose of the study within 10 seconds`);
    lines.push(`</Intro Section>`);
    lines.push(`<Scenario Section>`);
    lines.push(`Cover the scenario section in order:`);
    lines.push(`Take 30 seconds to go over the fire scenario`);
    lines.push(`For the first exit route, take 30 seconds to go over the details and 40 seconds to go over all the planning steps. Mention each planning step in order. Cover the planning step and any other follow up question for that planning step all together. Stick to the content provided and don’t make anything up.`);
    lines.push(`Take 10 seconds to go over the question of “Why multiple routes”`);
    lines.push(`Take 25 seconds to cover the second exit route along with any planning steps.`);
    lines.push(`Stick to the content provided and don’t make anything up. Make sure the discussion between the hosts is conversational, with one host asking questions and the other answering them`);
    lines.push(`</Scenario Section>`);
    lines.push(`<Outro Section>`);
    lines.push(`Take 30 seconds to cover this section`);
    lines.push(`</Outro Section>`);
    lines.push(`Finish the entire audio within 220 seconds`);
    lines.push(`</Instructions>`);
    lines.push(``);
    lines.push(``);
    lines.push(``);
    // --------------- Main Script ---------------
    lines.push(`<Main script>`);
    lines.push(``);
    lines.push(`<Intro Section>`);
    lines.push(`Purpose: This analysis is to understand the traffic congestion risks for ${startStreet} during wildfire evacuation and improve your preparedness`);
    lines.push(``);
    lines.push(`</Intro Section>`);
    lines.push(``);
    lines.push(`<Scenario Section>`);
    lines.push(`It is night time. The fire starts at Fairmont Ridge, with diablo winds from the North East moving towards your neighborhood.`);
    lines.push(`You are at ${startStreet}.`);
    lines.push(`You get an evacuation order to leave from the government. You were asleep a few minutes ago but now, you’re awake and have to get out.`);
    lines.push(``);
    lines.push(`We are assuming a worst case scenario where everyone in your neighborhood got this order and has to evacuate together.`);
    lines.push(`We realistically assume it takes at least 15 minutes to get ready and the first wave of cars to come out.`);
    lines.push(``);
    lines.push(`</Scenario Section>`);
    lines.push(``);
    lines.push(`<Question>`);
    lines.push(`What are some options to get out?`);
    lines.push(`</Question>`);
    lines.push(`<Answer>`);
    lines.push(``);
  
    // -------- Exit options (keep existing logic, with rounding + special blocks) --------
    enriched.forEach((r, sortedIndex) => {
      lines.push(`<Exit option>`);
      lines.push(``);
      lines.push(`<Name> ${r.name} </Name>`);
      if (r.tempName) {
        lines.push(`Temporary destination: ${r.tempName}`);
        lines.push(``);
      }
      if (r.routeNames.length > 0) {
        lines.push(`The route to this exit includes roads ${humanJoin(r.routeNames)}.`);
        lines.push(``);
      }
      lines.push(`<Details>`);
      lines.push(`On a normal day (free-flow): ${halfMin(r.totalFF)}`);
      lines.push(`During the wildfire (assuming you get out in the first wave): ${halfMin(r.total)}`);
      const extra = Math.max(0, r.total - r.totalFF);
      lines.push(`(Extra ${halfMin(extra)} due to congestion)`);
      if (sortedIndex === 0 && r.late > 0) {
        const lateDelta = Math.max(0, r.late - r.total);
        lines.push(
          `If you leave 10 minutes late, total evacuation time is ${halfMin(r.late)}${
            lateDelta > 0 ? ` (that’s an additional ${halfMin(lateDelta)} of travel time).\nSo if you leave 10 minutes late, you end up reaching the exit ${halfMin(lateDelta + 600)} late` : `.`
          }`
        );
      }
      lines.push(`</Details>`);
      lines.push(``);
  
      if (sortedIndex === 0) {
        // First exit: alerts + buddy + calm driving/go-bags (as per your example)
        lines.push(`<Planning steps>`);
        lines.push(``);
        lines.push(
          `How can we plan better to avoid this?\nAns: Subscribe to AC (Alameda County) Alerts and Nixle Alerts to leave on time and avoid further delays. Then, make sure to have your go-bags staged by the door to avoid delays while leaving.`
        );
        lines.push(``);
        lines.push(
          `Follow-up question: What if I cannot wake up on time during the alert?\nAns: Join a neighbourhood buddy group. If you’re asleep and didn’t receive the alert, your neighbour could wake you up as a backup. This is all about collaboration, not competition.`
        );
        lines.push(``);
        lines.push(
          `Follow-up question: If everyone evacuates on time within the first wave, won’t that increase the time during first wave?\nAns: Yes, but there are ways to handle that.\nFirstly, leave early if you live in a risky area (one-way in/out). You can leave during a red flag warning itself before the fire starts and stay with a relative.\nSecondly, governments attempt to avoid evacuating everyone at once by limiting evacuation to specific zones at risk. It is important to know your zone.\nLastly, if you do get caught in congestion, don't panic and stay calm. Abrupt maneuvers to get ahead can cause crashes and delays that put everyone at risk. Always follow what the authorities say.`
        );
        lines.push(``);
        lines.push(`</Planning steps>`);
        lines.push(``);
        if (enriched.length >= 2) {
          lines.push(`<Question> Should we consider another route? Why not just stop at 1 </Question>`);
          lines.push(``);
          lines.push(`<Answer>`);
          lines.push(
            `In a fire, knowing only one path is risky because the situation can change quickly — fire spread, smoke, or downed trees can block roads. The model’s details are for planning and won’t always match real-time conditions. Having at least two routes in mind lets you apply your best judgment when conditions shift.`
          );
          lines.push(`</Answer>`);
          lines.push(``);
        }
      } else if (sortedIndex === 1) {
        // Second exit: carpool section with rounded times/savings
        lines.push(`<Planning steps>`);
        lines.push(`Planning steps:`);
        lines.push(``);
        if (r.pooling > 0 && r.total > 0) {
          const savings = Math.max(0, r.total - r.pooling);
          lines.push(
            `If everyone takes one car per household, your time to evacuate reduces from ${halfMin(r.total)} to ${halfMin(r.pooling)} (saves ${halfMin(savings)}). Plan in advance to take one vehicle.`
          );
        } else {
          lines.push(
            `If everyone takes one car per household, overall congestion drops and evacuation times come closer to free-flow. Plan in advance to take one vehicle.`
          );
        }
        lines.push(
          `Follow-up Question: I might have 3 cars and I don’t want them to be damaged. Why should I only take one?\nAns: If you take 3 cars out, you increase congestion. If you increase congestion, risks of delays or accidents could be higher, which means it takes longer for firefighters to arrive and focus on fighting the fire. If firefighters cannot fight the fire quickly, it could get worse and end up consuming your house. So taking one car makes it easy for firefighters to come in and actually save your property.`
        );
        lines.push(`.`);
        lines.push(``);
        lines.push(`</Planning steps>`);
        lines.push(``);
      } else {
        // Any additional exits: short generic planning (optional)
        lines.push(`<Planning steps>`);
        lines.push(
          `Have multiple routes in mind and keep essentials ready. Leave early when warnings are issued to stay closer to free-flow time.`
        );
        lines.push(`</Planning steps>`);
        lines.push(``);
      }
  
      lines.push(`</Exit option>`);
      lines.push(``);
    });
  
    lines.push(`</Answer>`);
    lines.push(``);
    lines.push(`<Outro Section>`);
    lines.push(`<Next steps>`);
    lines.push(`To be able to leave on time, you need to be subscribed to AC alerts, have a go-bag ready, etc. All your other neighbours will be doing this, so don’t get left behind.`);
    lines.push(`Make sure to share this with neighbours and family. You can create a buddy system with them to help alert you in case you’re asleep and miss the official alert.`);
    lines.push(`</Next steps>`);
    lines.push(`<Safety disclaimer (final line)>`);
    lines.push(`“This is educational; during an active incident, always follow official evacuation orders and routes.”`);
    lines.push(`</Safety disclaimer (final line)>`);
    lines.push(`</Outro Section>`);
    lines.push(``);
    lines.push(`</Main script>`);
    lines.push(``);
    lines.push(`"""`);
  
    return lines.join("\n");
  }

  const DEST_NAME_MAP = {
    "2151": "Estudillo Avenue",
    "1047": "Grand Avenue",
    "28": "580 North (Highway) near Estudillo",
  };

  const formatHalfMinute = (sec) => {
    const mins = Math.max(0, Number(sec || 0) / 60);
    const r = Math.round(mins * 2) / 2;
    if (r >= 60) {
      const h = Math.floor(r / 60);
      const m = Math.round((r - h * 60) * 2) / 2;
      if (m === 0) return `${h} hr`;
      return `${h} hr ${m} min`;
    }
    return `${r % 1 === 0 ? r : r} min`;
  };

  const formatCeilMinute = (sec) => {
    const mins = Math.ceil(Math.max(0, Number(sec || 0)) / 60);
    if (mins >= 60) {
      const h = Math.floor(mins / 60);
      const m = mins - h * 60;
      if (m === 0) return `${h} hr`;
      return `${h} hr ${m} min`;
    }
    return `${mins} min`;
  };

  const asRouteArray = (v) => (Array.isArray(v) ? v : []);

  const getTotalFreeFlow = (segs) =>
    segs.reduce((sum, l) => sum + Number(l?.free_flow_traffic_on_route || 0), 0);

  const groupContiguousByStreet = (segs) => {
    const groups = [];
    segs.forEach((l) => {
      const name = (l?.link_name ?? "Unnamed Road").toString();
      const sim = Number(l?.time_taken_to_cross_route ?? 0);
      const ff = Number(l?.free_flow_traffic_on_route ?? 0);
      const last = groups[groups.length - 1];
      if (last && last.name === name) {
        last.links.push(l);
        last.simTime += sim;
        last.freeTime += ff;
      } else {
        groups.push({
          name,
          links: [l],
          simTime: sim,
          freeTime: ff,
          firstIndex: groups.length,
        });
      }
    });
    return groups;
  };

  export function getTravelRouteRows(experiences, options = {}) {
    const masterRoutes = options?.masterRoutes || {};
    const hasMasterRoutes = !!(masterRoutes && Object.keys(masterRoutes).length);
    const safe = Array.isArray(experiences) ? experiences : [];

    const rows = safe.map((dest, idx) => {
      const segs = asRouteArray(dest?.destination_experience);
      const groups = groupContiguousByStreet(segs);
      const firstNine = groups.slice(0, 9);
      const topByFF = [...firstNine]
        .sort((a, b) => (b.freeTime || 0) - (a.freeTime || 0))
        .slice(0, 6)
        .sort((a, b) => a.firstIndex - b.firstIndex);

      const routeNamesDefault = topByFF
        .map((g) => normalizeRouteName(g.name))
        .filter(Boolean);

      const rawId = (dest?.destination_id ?? dest?.destination_name ?? "Unknown Exit").toString();
      const override = rawId && masterRoutes[rawId];
      if (hasMasterRoutes && !override) return null;

      const rawName = (dest?.destination_name ?? rawId).toString();
      const exitName = (override?.title?.trim()) || DEST_NAME_MAP[rawName] || DEST_NAME_MAP[rawId] || rawName;
      const overrideLinks = (override?.links || [])
        .map(l => normalizeRouteName(l?.name))
        .filter(Boolean);
      const routeNames = dedupeRouteNames(
        overrideLinks.length ? overrideLinks : routeNamesDefault
      );
      const explicitOrder = Number(override?.order);

      const totalFF = getTotalFreeFlow(segs);
      const total = Number(dest?.total_destination_time || 0);
      const late = Number(dest?.total_destination_time_lateEvac || 0);
      const pooling = Number(dest?.total_destination_time_pooling || 0);
      const lateArrivalPenalty = late && total
        ? Math.max(0, late - total + 600)
        : 0;
      const freeFlowHalfMinuteBucket = Math.round(Math.max(0, totalFF) / 30);

      return {
        idx,
        order: Number.isFinite(explicitOrder) && explicitOrder > 0 ? explicitOrder : null,
        sortKeyFF: totalFF,
        freeFlowHalfMinuteBucket,
        lateArrivalPenalty,
        id: rawId,
        exitName,
        name: exitName,
        path: routeNames,
        routeNames,
        totalFF,
        total,
        late,
        pooling,
        leaveOnTime: total,
        fft: totalFF,
        leave10MinLate: late,
        oneCarPerHouse: pooling,
        segs,
      };
    });

    const filteredRows = rows.filter(Boolean);
    const hasExplicitOrder = filteredRows.some((row) => Number.isFinite(Number(row?.order)));

    return filteredRows
      .sort((a, b) => {
        if (hasExplicitOrder) {
          const aOrder = Number.isFinite(Number(a?.order)) ? Number(a.order) : Number.POSITIVE_INFINITY;
          const bOrder = Number.isFinite(Number(b?.order)) ? Number(b.order) : Number.POSITIVE_INFINITY;
          if (aOrder !== bOrder) return aOrder - bOrder;
        }

        const fftBucketDiff = (a.freeFlowHalfMinuteBucket || 0) - (b.freeFlowHalfMinuteBucket || 0);
        if (fftBucketDiff !== 0) return fftBucketDiff;

        const latePenaltyDiff = (b.lateArrivalPenalty || 0) - (a.lateArrivalPenalty || 0);
        if (latePenaltyDiff !== 0) return latePenaltyDiff;

        return (a.sortKeyFF || 0) - (b.sortKeyFF || 0);
      });
  }

  export function getRouteTablesFromRows(routeRows) {
    const rows = Array.isArray(routeRows) ? routeRows : [];

    return rows.map((r) => ({
      id: r.id,
      exitName: r.exitName,
      path: r.path,
      leaveOnTime: formatHalfMinute(r.leaveOnTime),
      fft: formatHalfMinute(r.fft),
      leave10MinLate: r.leave10MinLate > 0 ? formatHalfMinute(r.leave10MinLate) : formatHalfMinute(0),
      oneCarPerHouse: r.oneCarPerHouse > 0 ? formatHalfMinute(r.oneCarPerHouse) : formatHalfMinute(0),
      queuedVehiclesLate: (Array.isArray(r.segs) ? r.segs : []).map((l) => ({
        linkId: l?.route_id,
        queuedVehicles: Number(l?.queued_vehicles_lateEvac ?? 0)
      })),
      queuedVehiclesOnTime: (Array.isArray(r.segs) ? r.segs : []).map((l) => ({
        linkId: l?.route_id,
        queuedVehicles: Number(l?.traffic_faced_on_route ?? 0)
      })),
    }));
  }

  export function buildTravelNarrativeV4(experiences, startLoc, startTimeSec, options = {}) {
    const masterRoutes = options?.masterRoutes || {};
    const nodeId = String(options?.nodeId || "").trim() || "Unknown";
    const warningSystemName = "Contra Costa Community Warning System";
    const areaLabel = "El Sobrante Hills";
    const halfMin = formatCeilMinute;
    const emphasisTime = (sec) => halfMin(sec).replace(/ min$/, "");
    const humanJoin = (arr) => {
      if (!arr.length) return "the available evacuation roads";
      if (arr.length === 1) return arr[0];
      if (arr.length === 2) return `${arr[0]} and ${arr[1]}`;
      return `${arr.slice(0, -1).join(", ")} and ${arr[arr.length - 1]}`;
    };

    const pickStartStreet = (routes) => {
      const fromRoute = routes.find((r) => Array.isArray(r.routeNames) && r.routeNames[0])?.routeNames?.[0];
      if (fromRoute) return fromRoute;
      if (typeof startLoc === "string" && startLoc.trim()) return startLoc.trim();
      if (startLoc && typeof startLoc === "object") {
        const candidate =
          startLoc.street ||
          startLoc.name ||
          startLoc.label ||
          (typeof startLoc.address === "string" ? startLoc.address.split(",")[0] : "");
        if (candidate && String(candidate).trim()) return String(candidate).trim();
      }
      return "your street";
    };

    const enriched = Array.isArray(options?.routeRows)
      ? options.routeRows
      : getTravelRouteRows(experiences, { masterRoutes });

    const startStreet = pickStartStreet(enriched);
    const primary = enriched[0] || null;
    const alternate = enriched[1] || null;

    const primaryLateDelay = primary?.late && primary?.total
      ? Math.max(0, primary.late - primary.total + 600)
      : 0;
    const primaryTravelExtraLate = primary?.late && primary?.total
      ? Math.max(0, primary.late - primary.total)
      : 0;
    const primaryCongestion = primary ? Math.max(0, primary.total - primary.totalFF) : 0;
    const alternateCongestion = alternate ? Math.max(0, alternate.total - alternate.totalFF) : 0;
    const lines = [];
    lines.push(`"""`);
    lines.push(`<Instructions>`);
    lines.push(`Start off the intro as “Welcome to the wildfire evacuation walkthrough for ${startStreet}!”`);
    lines.push(`Introduce yourselves as Alex and Jordan, our AI host and guest.`);
    lines.push(`The content itself is in question-answer format. Please cover the question-answers exactly as it is in the content.`);
    lines.push(`Make sure the discussion between the hosts is conversational, with the host asking questions and the guest answering them.`);
    lines.push(`Cover the content in the same order that it is laid out in.`);
    lines.push(`Stick to the content provided and don’t make anything up.`);
    lines.push(`Don’t take more than 30 seconds on average per question answer pair. For longer question-answer pairs, clip it at 40 seconds and smaller question answer pairs can be at 20 seconds.`);
    lines.push(`For the very first question and answer, cover it verbatim from the content.`);
    lines.push(`For the very second question and answer, cover it verbatim from the content.`);
    lines.push(`Cover the disclaimer at the end in 10 seconds.`);
    lines.push(`</Instructions>`);
    lines.push(``);
    lines.push(`<Main script>`);
    lines.push(``);
    lines.push(`Topic: Wildfire evacuation walkthrough for ${startStreet} (${areaLabel}): Node ID ${nodeId}`);
    lines.push(``);
    lines.push(`Question 1: What are we discussing today, and why should the listener care?`);
    lines.push(``);
    lines.push(`Answer: Imagine this, there is a wildfire evacuation order. You live on ${startStreet}. ${
      primary && primaryLateDelay
        ? `If you leave 10 min late, you'll reach Highway I-80 ${halfMin(primaryLateDelay)} later than your neighbors who left on time.`
        : `If you leave late, the delay may be much larger than the time you waited.`
    }`);
    lines.push(primaryLateDelay ? `Not 10 min later. ${emphasisTime(primaryLateDelay)}.` : `Not just a little later. Longer.`);
    lines.push(`That's what our Digital Twin evacuation simulation says about your street, your block, your two ways out.`);
    lines.push(`I'm going to walk you through a wildfire evacuation scenario on ${startStreet} because once you've heard it, you will already have made half the decisions your future self won't have time to make.`);
    lines.push(``);
    lines.push(`Question 2: What is the scenario? Let's set context.`);
    lines.push(``);
    lines.push(`Answer: It's a Diablo wind night. Hot, dry air coming down off the hills, the kind of wind that turns a campfire into a wall. A fire starts northeast of Sobrante Ridge. You're asleep.`);
    lines.push(`At two fourty seven AM, your phone rings. That's the ${warningSystemName} with an evacuation order. You need to leave, now!`);
    lines.push(``);
    lines.push(`Question 3: If I look out of the window, would I see a wall of flames coming through the hills?`);
    lines.push(``);
    lines.push(`Answer: No, here's the thing nobody tells you. The fire doesn't arrive first. Smoke arrives first. Embers arrive first. The same wind that's pushing the flames is pushing burning debris a mile - sometimes several miles - ahead of the firefront. By the time you can see orange on the ridge, the air on your street is already dangerous to breathe, and your neighbor's lawn is already lighting up.`);
    lines.push(`This is why you need to pack N95 masks in your go-bag.`);
    lines.push(``);
    lines.push(`Question 4: What are some options to get out?`);
    lines.push(``);
    if (primary) {
      lines.push(`Answer: A familiar route is Highway I-80 via ${primary.name}.`);
      lines.push(`The route to this exit includes roads ${humanJoin(primary.routeNames)} all the way to its exit on Highway I-80.`);
      lines.push(``);
      lines.push(`Normal day (free-flow): ${halfMin(primary.totalFF)}`);
      lines.push(`During the wildfire (if you leave on time): ${halfMin(primary.total)}`);
      lines.push(`(Extra ${halfMin(primaryCongestion)} due to congestion)`);
      if (primary.late > 0) {
        lines.push(`If you leave 10 minutes late, total drive out is ${halfMin(primary.late)}. That's an additional ${halfMin(primaryTravelExtraLate)} of travel time.`);
        lines.push(`So the cost of leaving 10 minutes late is actually ${halfMin(primaryLateDelay)} in traffic.`);
      }
    } else {
      lines.push(`Answer: The model did not return a route for this location yet. Generate route tables first, then regenerate this script.`);
    }
    lines.push(``);
    lines.push(`Question 5: How to better plan for this?`);
    lines.push(``);
    lines.push(`Answer: Under an order, you don't have time for new chores or extra preparation. Leave immediately with your family and pets.`);
    lines.push(`First, get out of the high risk area. Your street, ${startStreet}, is at a Very High Fire Hazard Severity Zone because of steep slopes and thick brush. Reaching Valley View Road clears the riskiest part of your journey. But, you must get to the highway to be in a safer area - and to give space for other families who need that road too.`);
    lines.push(`Second, make sure to subscribe to Contra Costa's Community Warning System (CWS) Alerts and Nixle so you leave on time and avoid delays.`);
    lines.push(`Third, keep your go-bags - 24-hour survival, not valuables - staged by the door to avoid further delays while leaving.`);
    lines.push(``);
    lines.push(`Follow-up question: What if I cannot wake up on time during the alert?`);
    lines.push(`Answer: Form a neighbourhood buddy group with 2 or 3 neighbors. If you sleep through the alert or if you end up not receiving any alert, your buddy could wake you up as a backup. Alert systems fail, and your best chance is having multiple alerting options.`);
    lines.push(``);
    lines.push(`Question 6: But if everyone leaves on time, won't that increase the first wave?`);
    lines.push(`Answer: Yes - but there are ways to handle that.`);
    lines.push(`Leave early, when you get an evacuation warning. People who leave during the warning are not in your way during the order. Leave early especially if you live in a risky area, have pets, or have mobility challenges. You can pre-emptively leave during a Red Flag Warning itself, before the fire starts and stay with a relative or friend in a safer area.`);
    lines.push(`Second, first responders avoid getting everyone out at once by limiting evacuation to specific zones at risk. Know your evacuation zone like you know your zip code. Your evacuation zone is "RCH011".`);
    lines.push(`Lastly, if you do get caught in congestion, do not panic and stay calm. Abrupt maneuvers to get ahead can cause crashes and delays that put everyone at risk. Always follow what the authorities say. This is about collaboration, not competition.`);
    lines.push(``);
    lines.push(`Question 7: Should we consider another route? Why not just stop at 1`);
    lines.push(``);
    lines.push(`Answer: One route is a guess. Two or three routes is a plan. Spot fires, smoke, downed power lines, any of these closes a road in seconds. The evacuation simulation's details are for planning and won't always match real-time conditions. Having at least two routes and alternative paths in mind lets you apply your best judgment when conditions shift.`);
    lines.push(``);
    if (alternate) {
      lines.push(`Question 8: What is another exit?`);
      lines.push(`Answer: An alternate exit is ${alternate.name} towards Highway I-80.`);
      lines.push(`The route to this exit includes roads ${humanJoin(alternate.routeNames)}.`);
      lines.push(``);
      lines.push(`Normal day (free-flow): ${halfMin(alternate.totalFF)}`);
      lines.push(`During the wildfire (assuming you get out in the first wave): ${halfMin(alternate.total)}`);
      lines.push(`(Extra ${halfMin(alternateCongestion)} due to congestion)`);
      lines.push(``);
      lines.push(`Question 9: Give us another planning step here?`);
      if (alternate.pooling > 0) {
        lines.push(`Answer: Our Digital Twin shows that if everyone takes ONLY one car per household, your evacuation time for this route is ${halfMin(alternate.pooling)}. Plan in advance to take one vehicle.`);
      } else {
        lines.push(`Answer: Plan in advance to take one vehicle per household. Fewer vehicles means less congestion and more room for emergency response.`);
      }
      lines.push(``);
      lines.push(`Follow-up Question: I might have 2 cars and I don't want them to be damaged. Why should I only take one?`);
      lines.push(`Answer: Two cars on Amend Road or Valley View Road means a fire engine can't pass. One car means it can. That's the trade. If you take 2 cars out, you increase congestion.`);
      lines.push(`If you increase congestion, it takes longer for firefighters to arrive and focus on fighting the fire. So taking one car makes it easier for firefighters to come in and actually try to save your property.`);
      lines.push(``);
    }
    lines.push(`Outro Section`);
    lines.push(``);
    lines.push(`Next steps:`);
    lines.push(`Share this with neighbors and family. Plan ahead together - decide how an evacuation will happen depending on where everyone is, and create a buddy system. Fast fires leave no time. In a real incident, you may have limited access to power and the internet - you might not get any information at all except "go now."`);
    lines.push(`The household that left on time, in one car, with the go-bag ready - that's a household no firefighter has to come back for. That's how you help them sleep tomorrow night.`);
    lines.push(`Firefighters protect. You prepare.`);
    lines.push(``);
    lines.push(`Safety disclaimer (final line):`);
    lines.push(`"This is educational content based on a scientifically validated evacuation simulation that does not account for law enforcement traffic intervention. During an active incident, always follow first responders and official evacuation orders."`);
    lines.push(``);
    lines.push(`</Main script>`);
    lines.push(`"""`);

    return lines.join("\n");
  }


  export function getRouteTables(experiences, options = {}) {
    return getRouteTablesFromRows(getTravelRouteRows(experiences, options));
  }
  

 /**********************************************************************
   *  getTravelExperienceToDestinations
   *  ---------------------------------------------------------------
   *  Returns an array describing the “narrative” for every destination
   *  that is reachable from the user’s current location.
   *
   *  Each element:
   *  {
   *    destination_id,
   *    destination_direction,          // compass direction from user
   *    total_destination_time,         // seconds (selectedTime + API)
   *    destination_experience: [
   *      {
   *        route_id,
   *        direction_to_enter_route,   // compass direction of link
   *        time_taken_to_cross_route,  // seconds
   *        free_flow_traffic_on_route, // fft from CSV
   *        traffic_faced_on_route      // queued_vehicles from API
   *      }, …
   *    ]
   *  }
   *********************************************************************/
  
 export async function getTravelExperienceToDestinations({
    lat,
    lng,
    graphData,            // { nodes, adjList, links }
    destinationNodes,     // [{ nodeId }]
    selectedTime = 0,     // seconds start‑delay
    simulation,
    apiAbortSignal = null
  } = {}) {


    console.log("Here graphData", graphData)
  
    if (!graphData?.nodes || Object.keys(graphData.nodes).length === 0) return [];
  
    /* ---------- nearest graph node to user ---------- */
    let nearestNodeId = null, minD = Infinity;
    for (const n of Object.values(graphData.nodes)) {
      const d = haversineDistance(lat, lng, n.lat, n.lng);
      if (d < minD) { minD = d; nearestNodeId = n.id; }
    }
  
    /* ---------- Dijkstra ---------- */
    const { distances, previous } = dijkstra(graphData.adjList, nearestNodeId);
  
    /* quick link metadata lookup */
    const linkMeta = Object.create(null);
    graphData.links.forEach(l => { linkMeta[l.link_id] = l; });
  
    const experienceArr = [];
  
    for (const dest of destinationNodes) {
      if (distances[dest.nodeId] === Infinity) continue;
  
      /* node path & linkId chain */
      const nodePath = reconstructPath(previous, nearestNodeId, dest.nodeId);
      const linkIds = [];
      for (let i = 0; i < nodePath.length - 1; i++) {
        const edge = graphData.adjList[nodePath[i]]
          ?.find(e => e.node === nodePath[i + 1]);
        if (edge?.link?.link_id != null) linkIds.push(edge.link.link_id);
      }
  
      /* call traffic API */
      const apiRes = await getPathTimes(linkIds, selectedTime, simulation.id, true);
      if (!apiRes?.link_times) continue;
  
      /* build per‑link experience */
      const perLinkExp = [];
      let prevBearing = null;
  
      linkIds.forEach((id, idx) => {
        const meta = linkMeta[id] || {};
        const api  = apiRes.link_times[idx] || {};
  
        const coords = meta.geometry ? parseLineString(meta.geometry) : [];
        const currBearing = coords.length > 1
          ? bearing(coords[0].lat, coords[0].lng,
                    coords[coords.length - 1].lat, coords[coords.length - 1].lng)
          : (prevBearing ?? 0);
  
        const entryDir = idx === 0
          ? toCompass(currBearing)                         // first link: compass
          : turnInstruction(prevBearing, currBearing);     // subsequent: L/R/continue
  
        perLinkExp.push({
          route_id: id,
          link_name: linkMeta[id].name || "Unnamed road",    // << added
          direction_to_enter_route: entryDir,
          time_taken_to_cross_route: api.travel_time ?? null,
          free_flow_traffic_on_route: meta.fft ?? null,
          traffic_faced_on_route: api.queued_vehicles ?? null
        });
  
        prevBearing = currBearing;
      });
  
      const total_destination_time =
        (apiRes.total_time ??
         perLinkExp.reduce((s, l) => s + (l.time_taken_to_cross_route || 0), 0))
        + selectedTime;
  
      const destNode = graphData.nodes[dest.nodeId];
      const destDir  = toCompass(bearing(lat, lng, destNode.lat, destNode.lng));
  
      experienceArr.push({
        destination_id: dest.nodeId,
        destination_name: dest.name || `Node ${dest.nodeId}`, 
        destination_direction: destDir,
        total_destination_time,
        destination_experience: perLinkExp
      });
    }
  
    return experienceArr;
  }


  export async function getTravelExperiencesV2({
    lat,
    lng,
    graphData,            // { nodes, adjList, links }
    destinationNodes,     // [{ nodeId, name? }]
    selectedTime = 0,     // if your weights are in seconds, keep parity with v1
    simulation,
    apiAbortSignal = null,
    // knobs
    rho = 0.50,           // near-optimal band (+20%)
    delta = 0.50,         // require dissimilarity ≥ 0.5 (≤50% overlap)
    K = 5,                // alt routes to try (via penalization)
    earlyWindowCost = 300,        // early branching window (in weight units)
    earlySharedMaxFrac = 0.30,    // ≤30% shared within early window
    penaltyAlpha = 1000
  } = {}) {
    if (!graphData?.nodes || Object.keys(graphData.nodes).length === 0) return [];
  
    const nodesById = graphData.nodes ?? {};
    const adjList   = graphData.adjList ?? {};

    //destinationNodes = destinationNodes.filter(d => ['14','1047','2151'].includes(String(d.nodeId)));
    console.log("Dest nodes", destinationNodes)

    
    // 1) origin snap
    const originNodeId = getNearestGraphNodeId(lat, lng, nodesById);
    if (originNodeId == null) return [];
  
    // 2) pick anchor-constrained routes via your selector
    const selectedRoutes = selectTwoDiverseRoutes({
      graphData,
      nearestNodeId: originNodeId,
      dests: destinationNodes,
      selectedTime,
      // (optional) if you changed defaults in the selector:
      // anchorIds: [5064, 5502, 5567, 5212, 5765],
      // requiredDestIds: ['14', '1047', '2151', '28'],
    });
    if (!selectedRoutes.length) return [];

    // quick lookups
    const linkMeta = Object.create(null);
    (graphData.links || []).forEach(l => { linkMeta[l.link_id] = l; });

    // map destId -> name for nice labels
    const nameByDest = new Map(
      (destinationNodes || []).map(d => [String(d.nodeId), d.name || String(d.nodeId)])
    );

    // build experiences for the chosen routes (your existing per-link logic)
    const results = await Promise.all(selectedRoutes.map(async (sel) => {
      const linkIds = sel.link_ids || [];
      if (!linkIds.length) return null;

      const apiRes = await getPathTimes(linkIds, selectedTime, simulation?.id, true);
      if (!apiRes?.link_times) return null;

      //Car pooling paths
      const apiRes_pooling = await getPathTimes(linkIds, selectedTime, 'simulation_output_1760580569', true);
      if (!apiRes_pooling?.link_times) return null;

      //Car pooling paths
      const apiRes_late = await getPathTimes(linkIds, selectedTime + 600, simulation?.id, true);
      if (!apiRes_late?.link_times) return null;

      // console.log("Late evac", apiRes_late)


      const perLinkExp = [];
      let prevBearing = null;

      linkIds.forEach((id, idx) => {
        const meta = linkMeta[id] || {};
        const api  = apiRes.link_times[idx] || {};
        const api_pooling  = apiRes_pooling.link_times[idx] || {};
        const api_late = apiRes_late.link_times[idx] || {}

        const coords = meta.geometry ? parseLineString(meta.geometry) : [];
        const currBearing = coords.length > 1
          ? bearing(coords[0].lat, coords[0].lng,
                    coords[coords.length - 1].lat, coords[coords.length - 1].lng)
          : (prevBearing ?? 0);

        const entryDir = idx === 0 ? toCompass(currBearing)
                                  : turnInstruction(prevBearing, currBearing);

        perLinkExp.push({
          route_id: id,
          link_name: linkMeta[id]?.name || "Unnamed road",
          direction_to_enter_route: entryDir,
          time_taken_to_cross_route: api.travel_time ?? null,
          time_taken_to_cross_route_pooling: api_pooling.travel_time ?? null,
          time_taken_to_cross_route_lateEvac: api_late.travel_time ?? null,
          free_flow_traffic_on_route: meta.fft ?? null,
          traffic_faced_on_route: api.queued_vehicles ?? null,
          queued_vehicles_lateEvac: api_late.queued_vehicles ?? null 
        });

        prevBearing = currBearing;
      });

      const destId = String(sel.nodeId);
      const destNode = graphData.nodes?.[destId];
      const destDir  = destNode
        ? toCompass(bearing(lat, lng, destNode.lat, destNode.lng))
        : undefined;

      const total_destination_time =
        (apiRes.total_time ??
        perLinkExp.reduce((s, l) => s + (l.time_taken_to_cross_route || 0), 0))
        ;

        const total_destination_time_pooling = 
        (apiRes_pooling.total_time ??
          perLinkExp.reduce((s, l) => s + (l.time_taken_to_cross_route_pooling || 0), 0))
          ;

          const total_destination_time_lateEvac = 
          (apiRes_late.total_time ??
            perLinkExp.reduce((s, l) => s + (l.time_taken_to_cross_route_lateEvac || 0), 0))
            ;
        // console.log("Late time 1", total_destination_time_lateEvac)
        // console.log("Late time 2", apiRes_late.total_time) 

      return {
        destination_id: destId,
        destination_name: nameByDest.get(destId) || `Node ${destId}`,
        destination_direction: destDir,
        total_destination_time,
        total_destination_time_pooling,
        total_destination_time_lateEvac,
        destination_experience: perLinkExp
      };
    }));

    return results.filter(Boolean);
    
  }


  function pickTopKAnchorDestCombos(routes, K = 3) {
    const seen = new Set();                  // keys like "anchorId|destId"
    const out  = [];
    const sorted = [...routes].sort((a, b) => a.total_weight - b.total_weight);

    console.log("Final routes", sorted)
    let seen_dest = new Set()
    let seen_anchor = new Set()
  
    for (const r of sorted) {
      const key = `${r.anchor_link_id}|${r.nodeId}`;
      if (seen_anchor.has(`${r.anchor_link_id}`) || seen_dest.has(`${r.nodeId}`)) continue;           // already picked this anchor–dest combo
      seen_anchor.add(`${r.anchor_link_id}`);
      seen_dest.add(`${r.nodeId}`);
      out.push(r);
      if (out.length >= K) break;
    }
    return out;
  }

  export function selectTwoDiverseRoutes({
    graphData,
    nearestNodeId,
    dests,
    selectedTime = 0,
    anchorIds = [9414, 5212, 6755],
    requiredDestIds = ['1047', '2151', '28'], // 4 destinations
  } = {}) {
    if (!graphData?.adjList || !graphData?.nodes || !graphData?.links) return [];
  
    let adj = graphData.adjList;

    // links to remove completely from the graph
    let blockedLinkIds = new Set([58, 6756, 6759, 5503, 6757, 8799, 60, 14737].map(Number));
    if(String(nearestNodeId)=="1862")
    {
      blockedLinkIds = new Set([])
    }

    // prune edges that reference a blocked link_id
    const adjPruned = {};
    for (const [nid, edges] of Object.entries(adj || {})) {
      adjPruned[nid] = (edges || []).filter(
        (e) => !blockedLinkIds.has(Number(e?.link?.link_id))
      );
    }
    adj = adjPruned;
  
    // --- normalize / quick lookups ---
    const origin = String(nearestNodeId);
  
    // linkId -> row
    const linkById = Object.create(null);
    for (const l of graphData.links || []) linkById[Number(l.link_id)] = l;
  
    // keep only the required destinations (+ names), with one exception:
    // if origin is 2307, 817, or 2760, exclude destination '28'
    const blockForOrigins = new Set(['2307','817','2760', '2308', '2764']);
    const effectiveRequired = blockForOrigins.has(origin)
      ? requiredDestIds.map(String).filter(id => id !== '28')
      : requiredDestIds.map(String);
    const destSet = new Set(effectiveRequired);
    const destList = (dests || [])
      .filter(d => destSet.has(String(d.nodeId)))
      .map(d => ({ nodeId: String(d.nodeId), name: d.name || String(d.nodeId) }));
    if (!destList.length) return [];
  
    // helper: node path -> link_ids via current graph adjList
    const pathToLinkIdsLocal = (path) => {
      const ids = [];
      for (let i = 0; i < path.length - 1; i++) {
        const u = String(path[i]), v = String(path[i + 1]);
        const edge = (adj[u] || []).find(e => String(e.node) === v);
        if (edge?.link?.link_id != null) ids.push(edge.link.link_id);
        else return []; // invalid hop
      }
      return ids;
    };
  
    // ---------- (1) Per-anchor Dijkstra: v → dest; keep closest 2 ----------
    const perAnchorBestVD = new Map(); // anchorId -> [{destId, name, dist, pathVtoDest}]
    const reversePairs = [];           // to build blacklist (v->u) later
  
    for (const anchorIdRaw of anchorIds) {
      const anchorId = Number(anchorIdRaw);
      const L = linkById[anchorId];
      if (!L) continue;
  
      const u = String(L.start_node_id);
      const v = String(L.end_node_id);
      const w = Number(L.length ?? L.weight ?? 0);
      if (!Number.isFinite(w)) continue;
  
      //reversePairs.push([v, u]);      // we will forbid v->u in the origin Dijkstra
  
      // Dijkstra from v to all nodes
      const { distances: distFromV, previous: prevFromV } = dijkstra(adj, v);
  
      // Collect (reachable) destinations and their v->dest paths
      const vd = [];
      for (const d of destList) {
        const d2 = distFromV[d.nodeId];
        if (!Number.isFinite(d2)) continue;
  
        const p2 = reconstructPath(prevFromV, v, d.nodeId);
        if (!p2 || !p2.length) continue;
  
        vd.push({ destId: d.nodeId, name: d.name, dist: d2, pathVtoDest: p2 });
      }
  
      // keep closest 2
      vd.sort((a, b) => a.dist - b.dist);
      perAnchorBestVD.set(anchorId, vd.slice(0, 2));
  
      // also stash start/end/weight for later combination
      perAnchorBestVD.get(anchorId).u = u;
      perAnchorBestVD.get(anchorId).v = v;
      perAnchorBestVD.get(anchorId).w = w;
    }
  
    // If nothing reachable from anchors, bail
    if (![...perAnchorBestVD.values()].some(arr => arr.length)) return [];
  
    // ---------- (2) One origin Dijkstra with reverse-anchor edges removed ----------
    // Build a temporary adjList that filters out every (v->u) opposite to an anchor
    const blacklist = new Set(reversePairs.map(([a, b]) => `${a}|${b}`));
    const adjNoRev = {};
    for (const [nid, edges] of Object.entries(adj)) {
      adjNoRev[nid] = (edges || []).filter(e => !blacklist.has(`${String(nid)}|${String(e.node)}`));
    }
    const { distances: distFromOrigin, previous: prevFromOrigin } = dijkstra(adjNoRev, origin);
  
    // helper: rebuild origin->u in the filtered graph
    const pathOriginTo = (u) => reconstructPath(prevFromOrigin, origin, u);
  
    // ---------- (3) Combine origin→u + (u→v anchor hop) + v→dest; return all ----------
    const routes = [];
  
    for (const anchorIdRaw of anchorIds) {
      const anchorId = Number(anchorIdRaw);
      const top2 = perAnchorBestVD.get(anchorId);
      if (!top2 || !top2.length) continue;
  
      const u = top2.u, v = top2.v, w = top2.w;
  
      // Need origin->u reachable (with reverse-edge blacklisted)
      const d1 = distFromOrigin[u];
      if (!Number.isFinite(d1)) continue;
  
      const p1 = pathOriginTo(u);
      if (!p1 || !p1.length) continue;
  
      for (const { destId, name, dist: d2, pathVtoDest: p2 } of top2) {
        // Combine node path
        // p1 ends at u, anchor hop u->v, p2 starts at v
        const node_path = p1.concat([v], p2.slice(1));
  
        // Map to link_ids and ensure the actual anchor link is present at the u->v hop
        let link_ids = pathToLinkIdsLocal(node_path);
        if (!link_ids.length) continue;
  
        // If the u->v hop was mapped to a parallel edge, force the real anchor link id
        const uvIdx = node_path.findIndex((n, i) => i < node_path.length - 1 && node_path[i] === u && node_path[i + 1] === v);
        if (uvIdx >= 0 && !link_ids.includes(anchorId)) {
          const hasAnchorEdge =
            (adj[u] || []).some(e => String(e.node) === v && Number(e.link?.link_id) === anchorId);
          if (hasAnchorEdge && uvIdx < link_ids.length) {
            link_ids = [...link_ids];
            link_ids[uvIdx] = anchorId;
          }
        }
        // If still not present (rare), skip this route
        if (!link_ids.includes(anchorId)) continue;
  
        const total_weight = d1 + w + d2 + selectedTime;
  
        routes.push({
          nodeId: destId,
          name,
          node_path,
          link_ids,
          total_weight,
          anchor_link_id: anchorId,
        });
      }
    }

    let finalRoutes = pickTopKAnchorDestCombos(routes, 2);
  
    // Return ALL (up to 8) without trimming
    return finalRoutes;
  }



  export function selectTwoDiverseRoutesSimple({
    graphData,
    nearestNodeId,
    dests,
    selectedTime = 0,
    blockedLinkIds = [],
  
    // optional diversity knobs (keep simple defaults)
    delta = 0.5,
    maxAltTries = 10,
    earlyWindowCost = 300,
    earlySharedMaxFrac = 0.3
  } = {}) {
    if (!graphData?.adjList || !graphData?.nodes) return [];
  
    const origin = String(nearestNodeId);
  
    // 1) prune blocked links
    const blocked = new Set((blockedLinkIds || []).map(Number));
    const adj = {};
    for (const [nid, edges] of Object.entries(graphData.adjList)) {
      adj[nid] = blocked.size
        ? (edges || []).filter(e => !blocked.has(Number(e?.link?.link_id)))
        : (edges || []);
    }
  
    // tiny helper: node_path -> link_ids (self-contained, no mystery signatures)
    const nodePathToLinkIds = (nodePath, adjList) => {
      const ids = [];
      for (let i = 0; i < nodePath.length - 1; i++) {
        const u = String(nodePath[i]);
        const v = String(nodePath[i + 1]);
        const edge = (adjList[u] || []).find(e => String(e.node) === v);
        const lid = edge?.link?.link_id;
        if (lid == null) return [];
        ids.push(Number(lid));
      }
      return ids;
    };
  
    // run base Dijkstra once
    const { distances: dist0, previous: prev0 } = dijkstra(adj, origin);
  
    const out = [];
    const seenDest = new Set();
  
    for (const d of (dests || [])) {
      const destId = String(d.nodeId);
      if (!destId || seenDest.has(destId)) continue;
      seenDest.add(destId);
  
      if (!Number.isFinite(dist0[destId])) continue;
  
      // shortest route
      const p0 = reconstructPath(prev0, origin, destId);
      if (!p0?.length) continue;
  
      const ids0 = nodePathToLinkIds(p0, adj);
      if (!ids0.length) continue;
  
      out.push({
        nodeId: destId,
        name: d.name || destId,
        node_path: p0,
        link_ids: ids0,
        total_weight: dist0[destId] + selectedTime,
        alt_index: 0
      });
  
      // 2nd "diverse" route: try blocking one edge from the shortest path
      let bestAlt = null;
      const tries = Math.min(maxAltTries, ids0.length);
  
      for (let i = 0; i < tries; i++) {
        const u = String(p0[i]);
        const v = String(p0[i + 1]);
        const lid = Number(ids0[i]);
  
        // block only that single edge (u->v with that link_id)
        const adj2 = { ...adj };
        adj2[u] = (adj[u] || []).filter(
          e => !(String(e.node) === v && Number(e?.link?.link_id) === lid)
        );
  
        const { distances: dist2, previous: prev2 } = dijkstra(adj2, origin);
        if (!Number.isFinite(dist2[destId])) continue;
  
        const p2 = reconstructPath(prev2, origin, destId);
        if (!p2?.length) continue;
  
        const ids2 = nodePathToLinkIds(p2, adj2);
        if (!ids2.length) continue;
  
        // optional: reuse your existing diversity checks if you want
        if (typeof dissimilarEnoughByIds === "function" && !dissimilarEnoughByIds(ids0, ids2, delta)) {
          continue;
        }
        if (typeof earlyBranchingPass === "function" && !earlyBranchingPass(p0, p2, earlyWindowCost, earlySharedMaxFrac)) {
          continue;
        }
  
        const cand = {
          nodeId: destId,
          name: d.name || destId,
          node_path: p2,
          link_ids: ids2,
          total_weight: dist2[destId] + selectedTime,
          alt_index: 1
        };
  
        if (!bestAlt || cand.total_weight < bestAlt.total_weight) bestAlt = cand;
      }
  
      if (bestAlt) out.push(bestAlt);
    }
  
    return out;
  }



  export async function getTravelExperiencesSimple({
    lat,
    lng,
    graphData,
    destinationNodes,
    selectedTime = 0,
    simulation,
    oneCarSimulationId = "",
    apiAbortSignal = null,
  
    blockedLinkIds = [],
    delta = 0.50,
    maxAltTries = 10,
    earlyWindowCost = 300,
    earlySharedMaxFrac = 0.30,
    constrainedRoutes = {},
  } = {}) {
    if (!graphData?.nodes || Object.keys(graphData.nodes).length === 0) return [];
  
    const nodesById = graphData.nodes ?? {};
    const originNodeId = getNearestGraphNodeId(lat, lng, nodesById);
    if (originNodeId == null) return [];
  
    const constrainedByDest = constrainedRoutes && typeof constrainedRoutes === "object"
      ? constrainedRoutes
      : {};
    const constrainedSelectedRoutes = [];
    const constrainedDestIds = new Set();

    (destinationNodes || []).forEach((dest) => {
      const destId = String(dest?.nodeId ?? "").trim();
      const rawConstraint = destId ? constrainedByDest[destId] : null;
      const pathLinks = Array.isArray(rawConstraint?.pathLinks)
        ? rawConstraint.pathLinks.map((id) => String(id)).filter(Boolean)
        : [];
      if (!destId || !pathLinks.length) return;
      constrainedDestIds.add(destId);
      constrainedSelectedRoutes.push({
        nodeId: destId,
        name: dest?.name || destId,
        node_path: [],
        link_ids: pathLinks,
        total_weight: null,
        alt_index: 0,
        source: "constrained",
      });
    });

    const remainingDestinationNodes = (destinationNodes || []).filter((dest) => {
      const destId = String(dest?.nodeId ?? "").trim();
      return destId && !constrainedDestIds.has(destId);
    });

    const fallbackSelectedRoutes = remainingDestinationNodes.length
      ? selectTwoDiverseRoutesSimple({
      graphData,
      nearestNodeId: originNodeId,
          dests: remainingDestinationNodes,
      timeOffsetSec: selectedTime,   // ✅ updated
      blockedLinkIds,
      delta,
      maxAltTries,
      earlyWindowCost,
      earlySharedMaxFrac,
        })
      : [];

    const selectedRoutes = [...constrainedSelectedRoutes, ...fallbackSelectedRoutes];
    if (!selectedRoutes.length) return [];
  
    const linkMeta = Object.create(null);
    (graphData.links || []).forEach(l => { linkMeta[l.link_id] = l; });
  
    const nameByDest = new Map(
      (destinationNodes || []).map(d => [String(d.nodeId), d.name || String(d.nodeId)])
    );
  
    const results = await Promise.all(selectedRoutes.map(async (sel) => {
      const linkIds = sel.link_ids || [];
      if (!linkIds.length) return null;
  
      const apiRes = await getPathTimes(linkIds, selectedTime, simulation?.id, true);
      if (!apiRes?.link_times) return null;
  
      // ✅ late evac (10 min late)
      const apiRes_late = await getPathTimes(linkIds, selectedTime + 600, simulation?.id, true);
      if (!apiRes_late?.link_times) return null;

      const oneCarSimId = String(oneCarSimulationId || "").trim();
      const apiRes_pooling = oneCarSimId
        ? await getPathTimes(linkIds, selectedTime, oneCarSimId, true)
        : null;
  
      const perLinkExp = [];
      let prevBearing = null;
  
      linkIds.forEach((id, idx) => {
        const meta = linkMeta[id] || {};
        const api  = apiRes.link_times[idx] || {};
        const api_late = apiRes_late.link_times[idx] || {}; // ✅
  
        const coords = meta.geometry ? parseLineString(meta.geometry) : [];
        const currBearing = coords.length > 1
          ? bearing(coords[0].lat, coords[0].lng,
                    coords[coords.length - 1].lat, coords[coords.length - 1].lng)
          : (prevBearing ?? 0);
  
        const entryDir = idx === 0 ? toCompass(currBearing)
                                   : turnInstruction(prevBearing, currBearing);
  
        perLinkExp.push({
          route_id: id,
          link_name: linkMeta[id]?.name || "Unnamed road",
          direction_to_enter_route: entryDir,
          time_taken_to_cross_route: api.travel_time ?? null,
          free_flow_traffic_on_route: meta.fft ?? null,
          traffic_faced_on_route: api.queued_vehicles ?? null,
  
          // ✅ late fields
          time_taken_to_cross_route_lateEvac: api_late.travel_time ?? null,
          queued_vehicles_lateEvac: api_late.queued_vehicles ?? null,
        });
  
        prevBearing = currBearing;
      });
  
      const destId = String(sel.nodeId);
      const destNode = graphData.nodes?.[destId];
      const destDir = destNode
        ? toCompass(bearing(lat, lng, destNode.lat, destNode.lng))
        : undefined;
  
      const total_destination_time =
        (apiRes.total_time ??
          perLinkExp.reduce((s, l) => s + (l.time_taken_to_cross_route || 0), 0));
  
      const total_destination_time_lateEvac =  // ✅
        (apiRes_late.total_time ??
          perLinkExp.reduce((s, l) => s + (l.time_taken_to_cross_route_lateEvac || 0), 0));

      const total_destination_time_pooling = apiRes_pooling?.link_times
        ? (apiRes_pooling.total_time ??
          apiRes_pooling.link_times.reduce((s, l) => s + Number(l?.travel_time || 0), 0))
        : 0;
  
      return {
        destination_id: destId,
        destination_name: nameByDest.get(destId) || `Node ${destId}`,
        destination_direction: destDir,
  
        route_variant: sel.alt_index ?? 0,
        node_path: sel.node_path,
        link_ids: linkIds,
  
        total_destination_time,
        total_destination_time_lateEvac, // ✅
        total_destination_time_pooling,
        destination_experience: perLinkExp,
      };
    }));
  
    return results.filter(Boolean);
  }
